/**
 * ReportNCContext — ouverture GLOBALE de la modale « Signaler une non-conformité »
 * avec un objet source EXPLICITE (QUAL-001 §5.5).
 *
 * Jumeau de `TaskCreateContext` : depuis le menu ⋮ d'une LIGNE de tableau,
 * l'objet visé n'est pas la page courante — on passe donc la source explicitement.
 * Expose `openReportNC({type, id, label, contextHints?})` ; la modale est montée
 * une seule fois dans `AppLayout` et pré-remplie avec cette source.
 */

import { useCallback, useMemo, useState } from 'react'

import { NonConformityCreateDialog } from '@/components/quality/NonConformityCreateDialog'
import { useEntity } from '@/contexts/EntityContext'
import type { NCContext, NCSourceChannel, SourceObjectType } from '@/hooks/useQuality.types'
import { ReportNCContext, type ReportNCContextValue } from './ReportNCContext.helpers'

export interface PendingNCReport {
    type: SourceObjectType
    id: string
    label: string
    /** Canal d'origine ; `manual` par défaut. Voir `reportNCRowAction`. */
    channel?: NCSourceChannel
    contextHints?: NCContext | null
}

export function ReportNCProvider({ children }: { children: React.ReactNode }) {
    const [pending, setPending] = useState<PendingNCReport | null>(null)

    const openReportNC = useCallback((report: PendingNCReport) => setPending(report), [])
    const close = useCallback(() => setPending(null), [])

    // `openReportNC` est stable → pas de recomposition des `useMemo` de row-actions.
    const value = useMemo<ReportNCContextValue>(() => ({ openReportNC }), [openReportNC])

    return (
        <ReportNCContext.Provider value={value}>
            {children}
            <GlobalReportNCDialog pending={pending} onClose={close} />
        </ReportNCContext.Provider>
    )
}

/** Modale rendue uniquement quand une source est demandée (hooks au repos sinon). */
function GlobalReportNCDialog({
    pending,
    onClose,
}: {
    pending: PendingNCReport | null
    onClose: () => void
}) {
    const { currentEntity } = useEntity()
    if (!pending) return null
    return (
        <NonConformityCreateDialog
            entityId={currentEntity?.entityId ?? ''}
            open
            onOpenChange={(o) => {
                if (!o) onClose()
            }}
            source={{
                type: pending.type,
                id: pending.id,
                label: pending.label,
                channel: pending.channel,
            }}
            contextHints={pending.contextHints ?? null}
        />
    )
}
